import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import ReactModal from "react-modal";
import * as Papa from "papaparse";
import { t } from "i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faTimes } from "@fortawesome/free-solid-svg-icons";
import Button from "components/Button/Button";
import LoadCont from "components/LoadCont/LoadCont";
import InfosFetcher from "components/InfosFetcher/InfosFetcher";
import TagsList from "components/Tags/TagsList/TagsList";
import useAccount from "classes/Accounts/hooks/useAccount";
import genRequest from "includes/request";
import download_file from "includes/dl_file";
import formatDate from "includes/formatDate";
import { getUserProps } from "./UserProps";
import { useUsersContext } from "Pages/Contacts/providers/UsersProvider";
import ContactsPickerTable from "./ContactsPickerTable";
import { getUserId, fieldLabel, roleLabel, siteLabel, fetchProfessions, withProfessions } from "./exportUtils";
import "./ContactsExport.css";

const FIELDS_STORAGE_KEY = "contacts-export-fields";

function getStoredFields(availableIds) {
	try {
		const stored = JSON.parse(localStorage.getItem(FIELDS_STORAGE_KEY));
		if (Array.isArray(stored)) {
			const ids = stored.filter((id) => availableIds.includes(id));
			if (ids.length)
				return (ids);
		}
	} catch (e) { }
	return (availableIds);
}

function storeFields(ids) {
	try {
		localStorage.setItem(FIELDS_STORAGE_KEY, JSON.stringify(ids));
	} catch (e) { }
}

const toMap = (users) => new Map(users.map((u) => [getUserId(u), u]));

/**
 * Full window modal exporting contacts as a CSV file (";" separated, for Excel in french).
 * The user either checks contacts in the table of the modal (pre-checked with the
 * contacts checked on the page), or exports all the results of the page filters.
 */
export default function ContactsExport(props) {
	const { request, onClose } = props;
	const { account, isSup } = useAccount();
	const QCL = useQueryClient();
	const { selectedUsers, totalUsers, tableQuery } = useUsersContext();
	const fields = getUserProps(isSup);
	const fieldIds = fields.map((a) => a.id);
	const [picked, setPicked] = useState(() => toMap(selectedUsers));
	const [mode, setMode] = useState(selectedUsers.length ? "selection" : "all");
	const [checkedFields, setCheckedFields] = useState(() => getStoredFields(fieldIds));
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState(false);

	// Nothing picked anymore (ex: "Unselect all"): back to all the results
	useEffect(() => {
		if (!picked.size)
			setMode("all");
	}, [picked]);

	// Checking contacts in the table means exporting them
	const updatePicked = (updater) => {
		setError(false);
		setMode("selection");
		setPicked(updater);
	};

	const count = mode === "selection" ? picked.size : totalUsers;

	const toggleField = (id) => {
		setError(false);
		setCheckedFields((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);
	};

	const setAllFields = (checked) => {
		setError(false);
		setCheckedFields(checked ? fieldIds : []);
	};

	const getUsers = async () => {
		if (mode === "selection")
			return (Array.from(picked.values()));
		const resp = await request(1, totalUsers, tableQuery.orderBy, tableQuery.orderDirection);
		return (resp?.Items || []);
	};

	const genRows = async (users, columns) => {
		let sites = new Map();
		if (columns.some((a) => a.id === "SiteName")) {
			const resp = await genRequest("Sites/SitesOfCompany/" + account.CompanyId);
			(resp || []).forEach((site) => sites.set(site.SiteId, site));
		}
		if (columns.some((a) => a.id === "WFY" || a.id === "WFU")) {
			const professions = await fetchProfessions(QCL, genRequest, account.CompanyId);
			users = users.map((user) => withProfessions(user, professions));
		}
		return users.map((user) => columns.map((col) => {
			if (col.id === "SiteName")
				return (siteLabel(sites.get(user.SiteId)));
			if (col.id === "Role")
				return (roleLabel(user.Role));
			if (col.id === "LastConnexion")
				return (user.LastConnexion ? formatDate(user.LastConnexion, "D/M/Y h:m") : "");
			// Some values contain line breaks or extra spaces (ex: a function ending with "\n"),
			// Excel would display them on several lines
			const value = user[col.id] ?? "";
			return (typeof value === "string" ? value.replace(/\s*[\r\n]+\s*/g, " ").trim() : value);
		}));
	};

	const handleExport = async () => {
		// Keep the order of the fields list, not the checking order
		const columns = fields.filter((a) => checkedFields.includes(a.id));

		if (!columns.length)
			return setError(t("Contacts.NO_FIELDS_ERROR"));
		if (!count)
			return setError(t("Contacts.EXPORT_EMPTY"));
		setError(false);
		setIsExporting(true);
		try {
			const users = await getUsers();
			if (!users.length) {
				setIsExporting(false);
				return setError(t("Contacts.EXPORT_EMPTY"));
			}
			const rows = await genRows(users, columns);
			const csv = Papa.unparse({
				fields: columns.map(fieldLabel),
				data: rows,
			}, { delimiter: ";" });
			download_file(
				"\uFEFF" + csv,
				"contacts-" + formatDate(null, "Y-M-D") + ".csv",
				"text/csv;charset=utf-8;"
			);
			storeFields(columns.map((a) => a.id));
			toast(t("Contacts.EXPORT_SUCCESS", { count: users.length }), { autoClose: 3000 });
			setIsExporting(false);
			onClose();
		} catch (e) {
			console.error("ContactsExport", e);
			setIsExporting(false);
			setError(t("Contacts.EXPORT_ERROR"));
		}
	};

	return (
		<ReactModal
			isOpen={true}
			onRequestClose={() => !isExporting && onClose()}
			className="ContactsExport"
			overlayClassName="ReactModal__Overlay ContactsExport-overlay"
		>
			<div className="ContactsExport-head">
				<h4 className="m-0">{t("Contacts.EXPORT_MODAL_TITLE")}</h4>
				<Button
					className="unstyled ContactsExport-close w-auto"
					title={t("Commons.CLOSE")}
					onClick={onClose}
					disabled={isExporting}
				>
					<FontAwesomeIcon icon={faTimes} />
				</Button>
			</div>

			<div className="ContactsExport-main">
				<ContactsPickerTable picked={picked} setPicked={updatePicked} initialSearch={props.search} />

				<div className="ContactsExport-side">
					<div className="ContactsExport-body">
						<section className="ContactsExport-section">
							<h6>{t("Contacts.EXPORT_WHO")}</h6>
							<label className="ContactsExport-option">
								<input
									type="radio"
									name="contacts-export-mode"
									checked={mode === "selection"}
									onChange={() => setMode("selection")}
								/>
								<div>
									<div>{t("Contacts.EXPORT_PICKED", { count: picked.size })}</div>
									<div className="ContactsExport-hint m-0">{t("Contacts.EXPORT_PICK_HINT")}</div>
								</div>
							</label>
							<label className="ContactsExport-option">
								<input
									type="radio"
									name="contacts-export-mode"
									checked={mode === "all"}
									onChange={() => setMode("all")}
								/>
								<div>
									<div>{t("Contacts.EXPORT_ALL", { count: totalUsers })}</div>
									<ActiveFilters {...props} />
								</div>
							</label>
						</section>

						<section className="ContactsExport-section">
							<div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
								<h6 className="m-0">{t("Contacts.EXPORT_FIELDS")}</h6>
								<div className="d-flex gap-3">
									<Button className="unstyled ContactsExport-link w-auto" onClick={() => setAllFields(true)}>
										{t("Contacts.EXPORT_CHECK_ALL")}
									</Button>
									<Button className="unstyled ContactsExport-link w-auto" onClick={() => setAllFields(false)}>
										{t("Contacts.EXPORT_UNCHECK_ALL")}
									</Button>
								</div>
							</div>
							<div className="ContactsExport-fields">
								{fields.map((field) => (
									<label key={field.id} className="ContactsExport-field">
										<input
											type="checkbox"
											checked={checkedFields.includes(field.id)}
											onChange={() => toggleField(field.id)}
										/>
										{fieldLabel(field)}
									</label>
								))}
							</div>
						</section>
					</div>

					<div className="ContactsExport-foot">
						{error && <div className="ContactsExport-error">{error}</div>}
						<div className="ContactsExport-actions">
							<Button className="btn-bg w-auto" onClick={onClose} disabled={isExporting}>
								{t("Commons.CANCEL")}
							</Button>
							<Button className="btn-primary w-auto gap-2" onClick={handleExport} disabled={isExporting || !count}>
								{isExporting
									? <><LoadCont className="inline primary-bg" width={16} />{t("Contacts.EXPORT_IN_PROGRESS")}</>
									: <><FontAwesomeIcon icon={faFileExport} />{t("Contacts.EXPORT_DOWNLOAD", { count })}</>}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</ReactModal>
	);
}

function ActiveFilters(props) {
	const { account } = useAccount();
	let items = [];

	if (props.search)
		items.push(`${t("Contacts.SEARCH_RESULTS")} "${props.search}"`);
	if (props.showSaved)
		items.push(t("Contacts.FAVORITES_USERS"));
	if (props.userState)
		items.push(props.userState === "actives" ? t("Contacts.ACTIVE_USERS") : t("Contacts.INACTIVE_USERS"));
	if (props.site?.value)
		items.push(
			<InfosFetcher
				queryKey={["Sites", props.site.value]}
				queryFn={() => genRequest("Sites/" + props.site.value)}
				treat={(resp) => `${t("Contacts.BELONGING_SITE")} "${resp.Name}"`}
			/>
		);
	if (props.groups?.length && props.groups[0] !== void 0)
		items.push(
			<div className="d-flex flex-wrap align-items-center gap-1">
				{t("Contacts.PART_OF")} :
				<InfosFetcher
					queryKey={["Groups", "OfCompany", account.CompanyId]}
					queryFn={() => genRequest("Groups/OfCompany/" + account.CompanyId)}
					treat={(groups) => (
						<TagsList
							sortBy={"text"}
							items={(groups || [])
								.filter((a) => props.groups.includes(a.GroupId))
								.map((a) => ({ color: a.DefaultHexaColor, text: a.Name }))}
						/>
					)}
				/>
			</div>
		);

	if (!items.length)
		return (false);
	return (
		<div className="ContactsExport-hint">
			<div>{t("Contacts.EXPORT_ACTIVE_FILTERS")}</div>
			<ul className="m-0 ps-3">
				{items.map((a, key) => <li key={key}>{a}</li>)}
			</ul>
		</div>
	);
}
